import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { NUMBERS_WITH_KEYBOARD_KEYS, cn } from '@jobcost-monorepo/utils';
import {
  ChangeEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  useRef,
  useState,
} from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Icon, IconSvg, Input } from '../../atoms';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  errorName: string;
  type?: string;
  label?: string;
  width?: string;
}

export const InputForm: React.FC<Props> = ({
  name,
  errorName,
  type = 'text',
  label,
  width = 'w-full',
  ...rest
}) => {
  const { t } = useTranslation('errors');
  const { control, clearErrors } = useFormContext();
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({ control, name });

  const error = errors?.[errorName]?.message ?? '';
  const errorTranslated = t(error as string, { ns: 'errors' });

  const [passwordShown, setPasswordShown] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const checkIfNumber = (event: KeyboardEvent) => {
    if (type === 'number') {
      const key = event.key;
      if (!NUMBERS_WITH_KEYBOARD_KEYS.test(key)) {
        event.preventDefault();
      }
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!!errorName) {
      clearErrors(name);
    }
    let value = event.target.value;

    if (type === 'number') {
      value = value.replace(',', '.');
      if (value === '') {
        return onChange(null);
      }
      return onChange(parseFloat(value));
    }

    if (!value) return onChange(undefined);

    onChange(value);
  };

  const getInputType = (): string => {
    if (type === 'password') {
      return passwordShown ? 'text' : 'password';
    }

    return type;
  };

  const clearInput = () => {
    inputRef.current?.focus();
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={cn(width)}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          {label}
        </label>
      )}
      <div className="relative mt-1 rounded-md shadow-sm">
        <Input
          type={getInputType()}
          name={name}
          ref={inputRef}
          id={name}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() =>
            setTimeout(() => {
              setIsFocused(false);
            }, 150)
          }
          value={value || ''}
          onKeyDown={(event) => checkIfNumber(event)}
          className={cn(
            error && 'border-red text-red placeholder-red-300 dark:border-red',
          )}
          autoComplete="off"
          step={type === 'number' ? 'any' : undefined}
          {...rest}
        />
        {value && type !== 'password' && isFocused ? (
          <div className="flex justify-end items-center absolute inset-y-0 right-0 pr-3">
            <div
              className="rounded-2xl p-1 pointer-events-auto"
              onClick={clearInput}
            >
              <Icon
                className="hover:opacity-50 hover:cursor-pointer fill-black stroke-black dark:fill-white dark:stroke-white size-5"
                iconName="cancel"
              />
            </div>
          </div>
        ) : null}
        {value && type === 'password' ? (
          <div className="flex justify-end items-center absolute inset-y-0 right-0 pr-3">
            <div
              className="position-absolute z-1 cursor-pointer pr-1"
              onClick={() =>
                setPasswordShown && setPasswordShown(!passwordShown)
              }
            >
              <IconSvg
                name={passwordShown ? 'eyeSlashIcon' : 'eyeIcon'}
                size="4"
              />
            </div>
          </div>
        ) : null}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          {errorTranslated ? (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red"
              aria-hidden="true"
            />
          ) : (
            <span className="h-5 w-5 " />
          )}
        </div>
      </div>
      <p className="h-4 text-xs my-1 ml-1 text-red overflow-hidden" id="error">
        {errorTranslated}
      </p>
    </div>
  );
};

export default InputForm;
